import { Button, TextInput } from '@any-ui-react/core';
import { vestResolver } from '@hookform/resolvers/vest';
import { differenceInYears } from 'date-fns';
import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { ZodError } from 'zod';

import { DatePicker } from '@/common/components/shared/date-picker/DatePicker';
import { ModalFooter } from '@/common/components/shared/modal-footer/ModalFooter';
import { DependentRelationSelectSingle } from '@/common/components/shared/select/dependent-relation/DependentRelationSelect';
import { toast } from '@/common/components/shared/toaster/toast';
import {
  DependentFormData,
  type DependentFormDataType,
  EmployeeDependent,
  type EmployeeDependentType,
} from '@/common/types';
import { DateUtils, StringUtils } from '@/common/utils';

import { dependentFormValidationSuite } from './dependent-form.validator';

export interface DependentFormProps {
  dependent?: EmployeeDependentType;
  onSuccess: (data: EmployeeDependentType) => void;
  onCancel: () => void;
}

export const DependentForm = ({ dependent, onSuccess, onCancel }: DependentFormProps) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    register,
    setValue,
    clearErrors,
  } = useForm<DependentFormDataType>({
    defaultValues: DependentFormData.parse({ ...dependent }),
    resolver: vestResolver(dependentFormValidationSuite),
  });
  const dateOfBirth = useWatch({ control, name: 'dateOfBirth' });

  const onSubmit = useMemo(
    () =>
      handleSubmit(async formValues => {
        try {
          onSuccess(EmployeeDependent.parse({ ...formValues, id: dependent?.id }));
        } catch (e) {
          if (e instanceof ZodError) {
            const message = e.errors.map(it => `${it.path.join('.')}: ${it.message}`).join('\n');
            toast.error(message);
          }
        }
      }),
    [dependent?.id, handleSubmit, onSuccess],
  );

  return (
    <form noValidate={true}>
      <fieldset className="form-fieldset w-full">
        <TextInput
          {...register('name')}
          autoComplete="off"
          autoCorrect="off"
          className="col-span-2"
          data-1p-ignore={true}
          error={errors.name?.message}
          label="Name"
          placeholder="Name"
          required={true}
        />

        <Controller
          control={control}
          name="relation"
          render={({ field: { value } }) => (
            <div className="col-span-2 sm:col-span-1">
              <DependentRelationSelectSingle
                error={errors.relation?.message}
                label="Relation"
                required={true}
                value={value}
                onChange={newValue => {
                  setValue('relation', newValue?.value ?? null, { shouldDirty: true });
                  clearErrors('relation');
                }}
              />
            </div>
          )}
        />

        <div className="col-span-2 sm:col-span-1">
          <DatePicker
            error={errors.dateOfBirth?.message}
            label="Date of Birth"
            placeholder="yyyy-MM-dd"
            required={true}
            value={dateOfBirth || undefined}
            onChange={value => {
              setValue('dateOfBirth', DateUtils.formatDate(value), { shouldDirty: true });
              clearErrors('dateOfBirth');
            }}
          />
          {dateOfBirth && (
            <div className="text-gray-7 text-xs ml-3">
              {StringUtils.withPluralize(differenceInYears(new Date(), new Date(dateOfBirth || '')), 'year')} old
            </div>
          )}
        </div>
      </fieldset>

      <ModalFooter spacerSize={-1}>
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="bg-blue-6" loading={isSubmitting} type="submit" onClick={onSubmit}>
          Save
        </Button>
      </ModalFooter>
    </form>
  );
};
